import React from "react";
import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
    LayoutDashboard, Package, Layers, ShoppingBag, Tag, Image, Percent, Calendar,
    Settings, Palette, Mail, FileText, Camera, Star, MessageSquare, LogOut,
    ExternalLink, LifeBuoy, Bot, ClipboardList, Boxes, UserCircle, Wrench,
    Upload, History, Menu, X, Receipt, Activity, Users, ChevronRight, RotateCcw,
    BarChart2, Truck, Zap, Award, BookOpen
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useSettings } from "../../contexts/SettingsContext";
import NotificationBell from "../../components/NotificationBell";
import { MEDIA } from "../../lib/api";

const NAV_GROUPS = [
    {
        label: null,
        items: [
            { path: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true, roles: ["admin", "superadmin", "sales", "installer"] },
        ]
    },
    {
        label: "Comercial",
        items: [
            { path: "/admin/products", label: "Produse", icon: Package, roles: ["admin", "superadmin", "sales"] },
            { path: "/admin/inventory", label: "Inventar", icon: Boxes, roles: ["admin", "superadmin", "sales"] },
            { path: "/admin/categories", label: "Categorii", icon: Layers, roles: ["admin", "superadmin"] },
            { path: "/admin/packages", label: "Pachete", icon: ShoppingBag, roles: ["admin", "superadmin", "sales"] },
            { path: "/admin/orders", label: "Comenzi", icon: Tag, roles: ["admin", "superadmin", "sales"] },
            { path: "/admin/returns", label: "Retururi", icon: RotateCcw, roles: ["admin", "superadmin", "sales"] },
            { path: "/admin/invoices", label: "Facturi & Proforme", icon: Receipt, roles: ["admin", "superadmin", "sales"] },
            { path: "/admin/suppliers", label: "Furnizori", icon: Truck, roles: ["admin", "superadmin", "sales"] },
            { path: "/admin/accounting", label: "Contabilitate", icon: BookOpen, roles: ["admin", "superadmin"] },
            { path: "/admin/analytics", label: "Analytics", icon: BarChart2, roles: ["admin", "superadmin", "sales"] },
            { path: "/admin/bulk-ops", label: "Operațiuni masă", icon: Zap, roles: ["admin", "superadmin"] },
            { path: "/admin/discounts", label: "Coduri discount", icon: Percent, roles: ["admin", "superadmin", "sales"] },
            { path: "/admin/promotions", label: "Promoții", icon: Tag, roles: ["admin", "superadmin", "sales"] },
            { path: "/admin/loyalty", label: "Puncte fidelizare", icon: Award, roles: ["admin", "superadmin"] },
        ]
    },
    {
        label: "Operațiuni",
        items: [
            { path: "/admin/tickets", label: "Tickete", icon: LifeBuoy, roles: ["admin", "superadmin", "sales", "installer"] },
            { path: "/admin/agenda", label: "Agenda / CRM", icon: ClipboardList, roles: ["admin", "superadmin", "sales", "installer"] },
            { path: "/admin/appointments", label: "Programări", icon: Calendar, roles: ["admin", "superadmin", "sales", "installer"] },
            { path: "/admin/clients", label: "Clienți", icon: UserCircle, roles: ["admin", "superadmin", "sales"] },
            { path: "/admin/users", label: "Staff", icon: Users, roles: ["admin", "superadmin"] },
            { path: "/admin/services-edit", label: "Servicii", icon: Wrench, roles: ["admin", "superadmin"] },
        ]
    },
    {
        label: "Conținut",
        items: [
            { path: "/admin/banners", label: "Bannere", icon: Image, roles: ["admin", "superadmin"] },
            { path: "/admin/reviews", label: "Recenzii", icon: Star, roles: ["admin", "superadmin", "sales"] },
            { path: "/admin/blog", label: "Blog", icon: FileText, roles: ["admin", "superadmin"] },
            { path: "/admin/portfolio", label: "Portofoliu", icon: Camera, roles: ["admin", "superadmin"] },
            { path: "/admin/newsletter", label: "Newsletter", icon: Mail, roles: ["admin", "superadmin", "sales"] },
            { path: "/admin/messages", label: "Mesaje", icon: MessageSquare, roles: ["admin", "superadmin", "sales"] },
            { path: "/admin/chat", label: "Asistent AI", icon: Bot, roles: ["admin", "superadmin"] },
        ]
    },
    {
        label: "Sistem",
        items: [
            { path: "/admin/import", label: "Import / Export", icon: Upload, roles: ["admin", "superadmin"] },
            { path: "/admin/audit-log", label: "Audit log", icon: History, roles: ["admin", "superadmin"] },
            { path: "/admin/settings", label: "Setări", icon: Settings, roles: ["admin", "superadmin"] },
            { path: "/admin/theme", label: "Temă vizuală", icon: Palette, roles: ["admin", "superadmin"] },
            { path: "/admin/health", label: "Health", icon: Activity, roles: ["admin", "superadmin"] },
        ]
    },
];

export default function AdminLayout() {
    const { user, logout } = useAuth();
    const { settings } = useSettings();
    const navigate = useNavigate();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = React.useState(false);

    React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    const userRole = user?.role || "";
    const visibleGroups = React.useMemo(() => {
        if (!userRole) return [];
        return NAV_GROUPS.map(g => ({
            ...g,
            items: g.items.filter(n => !n.roles || n.roles.includes(userRole))
        })).filter(g => g.items.length > 0);
    }, [userRole]);

    const allItems = visibleGroups.flatMap(g => g.items);
    const currentLabel = allItems.find(n => n.end ? location.pathname === n.path : location.pathname.startsWith(n.path))?.label || "Admin";

    const siteName = settings?.site_name || settings?.company_name || "ccashop";
    const logoUrl = settings?.logo_url ? (settings.logo_url.startsWith("http") ? settings.logo_url : MEDIA(settings.logo_url)) : "";
    const logoInitial = (siteName || "C").charAt(0).toUpperCase();

    return (
        <div className="min-h-screen lg:grid lg:grid-cols-[260px_1fr] bg-background" data-testid="admin-layout">
            {/* Mobile top bar */}
            <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between gap-2 bg-foreground text-background px-4 py-3 border-b border-background/20">
                <button onClick={() => setMobileOpen(true)} className="p-2 hover:bg-background/10 rounded-lg" data-testid="admin-menu-open">
                    <Menu className="h-5 w-5" />
                </button>
                <div className="flex items-center gap-2">
                    {logoUrl
                        ? <img src={logoUrl} alt={siteName} className="h-7 w-auto max-w-[90px] object-contain" />
                        : <div className="w-7 h-7 bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold rounded-lg text-sm">{logoInitial}</div>}
                    <span className="font-medium text-sm truncate max-w-[140px]">{currentLabel}</span>
                </div>
                <div className="text-background"><NotificationBell /></div>
            </div>

            {mobileOpen && <div className="lg:hidden fixed inset-0 bg-foreground/70 z-40 backdrop-blur-sm" onClick={() => setMobileOpen(false)} data-testid="admin-menu-backdrop" />}

            {/* Sidebar */}
            <aside
                className={`bg-[hsl(20,14%,10%)] text-background flex flex-col fixed lg:sticky lg:top-0 top-0 left-0 bottom-0 z-50 w-[270px] lg:w-auto lg:h-screen transition-transform duration-200 ${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 overflow-hidden`}
                data-testid="admin-sidebar">

                {/* Brand */}
                <div className="px-5 py-4 flex items-center justify-between border-b border-white/8 flex-shrink-0">
                    <Link to="/admin" className="flex items-center gap-3">
                        {logoUrl
                            ? <img src={logoUrl} alt={siteName} className="h-9 w-auto max-w-[110px] object-contain opacity-90" />
                            : <div className="w-9 h-9 bg-primary text-primary-foreground flex items-center justify-center font-heading font-bold rounded-xl text-base">{logoInitial}</div>}
                        <div>
                            <div className="font-semibold text-sm truncate max-w-[140px]">{siteName}</div>
                            <div className="text-[10px] text-background/40 uppercase tracking-widest">Admin Panel</div>
                        </div>
                    </Link>
                    <button onClick={() => setMobileOpen(false)} className="lg:hidden text-background/60 hover:text-background p-1" data-testid="admin-menu-close">
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Nav */}
                <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4 no-scrollbar">
                    {visibleGroups.map((group, gi) => (
                        <div key={gi}>
                            {group.label && (
                                <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-background/35">
                                    {group.label}
                                </div>
                            )}
                            <div className="space-y-0.5">
                                {group.items.map(n => (
                                    <NavLink key={n.path} to={n.path} end={n.end}
                                        className={({ isActive }) =>
                                            `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${isActive
                                                ? "bg-primary/15 text-primary"
                                                : "text-background/60 hover:text-background hover:bg-white/6"}`}
                                        data-testid={`admin-nav-${n.label.toLowerCase().replace(/[\s/&]/g, "-")}`}>
                                        <n.icon className="h-4 w-4 flex-shrink-0" />
                                        <span className="truncate">{n.label}</span>
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                    ))}
                </nav>

                {/* Footer */}
                <div className="px-3 py-3 border-t border-white/8 flex-shrink-0">
                    <div className="text-xs text-background/40 truncate px-2 mb-2">{user?.email}</div>
                    <div className="flex gap-1">
                        <Link to="/"
                            className="flex-1 flex items-center justify-center gap-1.5 text-xs text-background/50 hover:text-background hover:bg-white/6 rounded-xl px-3 py-2 transition-all">
                            <ExternalLink className="h-3.5 w-3.5" /> Site
                        </Link>
                        <button
                            onClick={() => { logout(); navigate("/"); }}
                            className="flex-1 flex items-center justify-center gap-1.5 text-xs text-background/50 hover:text-destructive hover:bg-destructive/10 rounded-xl px-3 py-2 transition-all"
                            data-testid="admin-logout">
                            <LogOut className="h-3.5 w-3.5" /> Logout
                        </button>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <main className="overflow-y-auto min-w-0">
                <div className="hidden lg:flex items-center justify-between px-6 py-3 border-b border-border sticky top-0 bg-background/95 backdrop-blur-sm z-20">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{currentLabel}</span>
                    </div>
                    <NotificationBell />
                </div>
                <div className="p-4 md:p-6 lg:p-8">
                    <Outlet />
                </div>
            </main>
        </div>
    );
}
